<!--
  * Path: packages/frontend/src/core/components/app/AppSidebar.vue
  * Task: Navigation drawer.
  * Update: Injected the new Star Map route into the Maps section.
-->
<template>
  <v-navigation-drawer
    v-model="sidebar"
    :rail="authStore.isSidebarMinimized"
    permanent
    border="none"
    elevation="0"
    color="background"
    class="sidebar-shadow"
  >
    <!-- MAIN LIST -->
    <v-list v-if="isAppLoaded" density="compact" nav class="mt-2" v-model:opened="openedMainGroups">
      <v-list-item
        prepend-icon="mdi-home-variant-outline"
        title="Home"
        :value="'home'"
        :active="uiStore.activeView === 'home'"
        @click="uiStore.setActiveView('home')"
        color="primary"
        rounded="lg"
        class="mb-1"
      ></v-list-item>
      <v-list-item
        v-if="projectStore.loadedProject && canReadProject"
        prepend-icon="mdi-view-dashboard-outline"
        title="Dashboard"
        :value="'dashboard'"
        :active="uiStore.activeView === 'dashboard'"
        @click="uiStore.setActiveView('dashboard')"
        color="primary"
        rounded="lg"
        class="mb-1"
      ></v-list-item>
      <v-list-item
        v-if="projectStore.loadedProject && canReadProject"
        prepend-icon="mdi-application-edit-outline"
        title="Browser"
        :value="'editor'"
        :active="uiStore.activeView === 'editor'"
        @click="uiStore.setActiveView('editor')"
        color="primary"
        rounded="lg"
        class="mb-1"
      ></v-list-item>

      <!-- MAPS GROUP -->
      <v-list-group value="maps" v-if="projectStore.loadedProject && canReadProject && !authStore.isSidebarMinimized">
        <template v-slot:activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-map-outline"
            title="Maps"
            color="primary"
            rounded="lg"
            class="font-weight-medium mb-1"
          ></v-list-item>
        </template>
        <v-list-item prepend-icon="mdi-spider-web" title="Web Map" :value="'mapWeb'" :active="uiStore.activeView === 'mapWeb'" @click="uiStore.setActiveView('mapWeb')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-timeline-outline" title="Sequence Map" :value="'mapSequence'" :active="uiStore.activeView === 'mapSequence'" @click="uiStore.setActiveView('mapSequence')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-star-shooting-outline" title="Star Map" :value="'mapStar'" :active="uiStore.activeView === 'mapStar'" @click="uiStore.setActiveView('mapStar')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-radar" title="Impact Map" :value="'impactMap'" :active="uiStore.activeView === 'impactMap'" @click="uiStore.setActiveView('impactMap')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
      </v-list-group>

      <!-- Minimized Maps Menu -->
      <v-menu v-if="projectStore.loadedProject && canReadProject && authStore.isSidebarMinimized" location="end" open-on-hover offset="10">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" prepend-icon="mdi-map-outline" title="Maps" :active="isMapsGroupActive" color="primary" rounded="lg" class="font-weight-medium mb-1"></v-list-item>
        </template>
        <v-list density="compact" nav class="bg-surface" elevation="4" rounded="lg" min-width="200">
          <v-list-item prepend-icon="mdi-spider-web" title="Web Map" :value="'mapWeb'" :active="uiStore.activeView === 'mapWeb'" @click="uiStore.setActiveView('mapWeb')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-timeline-outline" title="Sequence Map" :value="'mapSequence'" :active="uiStore.activeView === 'mapSequence'" @click="uiStore.setActiveView('mapSequence')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-star-shooting-outline" title="Star Map" :value="'mapStar'" :active="uiStore.activeView === 'mapStar'" @click="uiStore.setActiveView('mapStar')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-radar" title="Impact Map" :value="'impactMap'" :active="uiStore.activeView === 'impactMap'" @click="uiStore.setActiveView('impactMap')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        </v-list>
      </v-menu>

      <!-- REPORTS GROUP -->
      <v-list-group value="reports" v-if="projectStore.loadedProject && canReadProject && !authStore.isSidebarMinimized">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" prepend-icon="mdi-chart-box-outline" title="Reports" color="primary" rounded="lg" class="font-weight-medium mb-1"></v-list-item>
        </template>
        <v-list-item prepend-icon="mdi-view-column-outline" title="Kanban Board" :value="'kanban'" :active="uiStore.activeView === 'kanban'" @click="uiStore.setActiveView('kanban')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-grid" title="Matrix Report" :value="'matrix'" :active="uiStore.activeView === 'matrix'" @click="uiStore.setActiveView('matrix')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-fish" title="Ishikawa Diagram" :value="'mapIshikawa'" :active="uiStore.activeView === 'mapIshikawa'" @click="uiStore.setActiveView('mapIshikawa')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-calendar" title="Calendar" :value="'calendar'" :active="uiStore.activeView === 'calendar'" @click="uiStore.setActiveView('calendar')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        <v-list-item prepend-icon="mdi-badge-account-outline" title="Role Profiles" :value="'roledescription'" :active="uiStore.activeView === 'roledescription'" @click="uiStore.setActiveView('roledescription')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
      </v-list-group>

      <!-- Minimized Reports Menu -->
      <v-menu v-if="projectStore.loadedProject && canReadProject && authStore.isSidebarMinimized" location="end" open-on-hover offset="10">
        <template v-slot:activator="{ props }">
          <v-list-item v-bind="props" prepend-icon="mdi-chart-box-outline" title="Reports" :active="isReportsGroupActive" color="primary" rounded="lg" class="font-weight-medium mb-1"></v-list-item>
        </template>
        <v-list density="compact" nav class="bg-surface" elevation="4" rounded="lg" min-width="200">
          <v-list-item prepend-icon="mdi-view-column-outline" title="Kanban Board" :value="'kanban'" :active="uiStore.activeView === 'kanban'" @click="uiStore.setActiveView('kanban')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-grid" title="Matrix Report" :value="'matrix'" :active="uiStore.activeView === 'matrix'" @click="uiStore.setActiveView('matrix')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-fish" title="Ishikawa Diagram" :value="'mapIshikawa'" :active="uiStore.activeView === 'mapIshikawa'" @click="uiStore.setActiveView('mapIshikawa')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-calendar" title="Calendar" :value="'calendar'" :active="uiStore.activeView === 'calendar'" @click="uiStore.setActiveView('calendar')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-badge-account-outline" title="Role Profiles" :value="'roledescription'" :active="uiStore.activeView === 'roledescription'" @click="uiStore.setActiveView('roledescription')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        </v-list>
      </v-menu>
    </v-list>

    <!-- APPEND LIST -->
    <template v-slot:append>
      <v-list v-if="isAppLoaded" density="compact" nav v-model:opened="openedAppendGroups">
        <v-list-item v-if="projectStore.loadedProject && canManageProjectSettings" prepend-icon="mdi-cog-outline" title="Project Settings" :value="'customize'" :active="uiStore.activeView === 'customize'" @click="uiStore.setActiveView('customize')" color="primary" rounded="lg" class="mb-1"></v-list-item>

        <v-list-group value="settings" v-if="isAppAdmin && !authStore.isSidebarMinimized">
          <template v-slot:activator="{ props }"><v-list-item v-bind="props" prepend-icon="mdi-application-cog-outline" title="App Settings" color="primary" rounded="lg" class="font-weight-medium mb-1"></v-list-item></template>
          <v-list-item prepend-icon="mdi-folder-cog-outline" title="Projects" :value="'projectAdministration'" :active="uiStore.activeView === 'projectAdministration'" @click="uiStore.setActiveView('projectAdministration')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-account-cog-outline" title="Users" :value="'userSettings'" :active="uiStore.activeView === 'userSettings'" @click="uiStore.setActiveView('userSettings')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-database-cog-outline" title="Data" :value="'dataSettings'" :active="uiStore.activeView === 'dataSettings'" @click="uiStore.setActiveView('dataSettings')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
        </v-list-group>

        <!-- Minimized Settings Menu -->
        <v-menu v-if="isAppAdmin && authStore.isSidebarMinimized" location="end" open-on-hover offset="10">
          <template v-slot:activator="{ props }"><v-list-item v-bind="props" prepend-icon="mdi-cogs" title="App Settings" :active="isSettingsGroupActive" color="primary" rounded="lg" class="font-weight-medium mb-1"></v-list-item></template>
          <v-list density="compact" nav class="bg-surface" elevation="4" rounded="lg" min-width="200">
            <v-list-item prepend-icon="mdi-folder-cog-outline" title="Projects" :value="'projectAdministration'" :active="uiStore.activeView === 'projectAdministration'" @click="uiStore.setActiveView('projectAdministration')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
            <v-list-item prepend-icon="mdi-account-cog-outline" title="Users" :value="'userSettings'" :active="uiStore.activeView === 'userSettings'" @click="uiStore.setActiveView('userSettings')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
            <v-list-item prepend-icon="mdi-database-cog-outline" title="Data" :value="'dataSettings'" :active="uiStore.activeView === 'dataSettings'" @click="uiStore.setActiveView('dataSettings')" color="primary" rounded="lg" class="text-body-2 mb-1"></v-list-item>
          </v-list>
        </v-menu>
      </v-list>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useUiStore } from '@/store/ui';
import { useProjectStore } from '@/store/project';
import { usePermissions } from '@/core/composables/usePermissions';

const authStore = useAuthStore();
const uiStore = useUiStore();
const projectStore = useProjectStore();
const { isAppAdmin, canManageProjectSettings, canReadProject } = usePermissions();

const sidebar = ref<boolean>(true);

// Separated State to prevent Vuetify List State Freezing
const openedMainGroups = ref<string[]>([]);
const openedAppendGroups = ref<string[]>([]);

const isAppLoaded = computed<boolean>(() => !!authStore.appData?.version);

const isMapsGroupActive = computed<boolean>(() => ['mapWeb', 'mapSequence', 'impactMap', 'mapStar'].includes(uiStore.activeView));
const isReportsGroupActive = computed<boolean>(() => ['kanban', 'matrix', 'mapIshikawa', 'calendar', 'roledescription'].includes(uiStore.activeView));
const isSettingsGroupActive = computed<boolean>(() => ['projectAdministration', 'userSettings', 'dataSettings'].includes(uiStore.activeView));

watch(() => uiStore.activeView, (newView: string) => {
  // Sync Main Groups
  if (['kanban', 'matrix', 'mapIshikawa', 'calendar', 'roledescription'].includes(newView) && !openedMainGroups.value.includes('reports')) openedMainGroups.value.push('reports');
  if (['mapWeb', 'mapSequence', 'impactMap', 'mapStar'].includes(newView) && !openedMainGroups.value.includes('maps')) openedMainGroups.value.push('maps');

  // Sync Append Groups
  if (['projectAdministration', 'userSettings', 'dataSettings'].includes(newView) && !openedAppendGroups.value.includes('settings')) openedAppendGroups.value.push('settings');
}, { immediate: true });
</script>

<style scoped>
.sidebar-shadow {
  box-shadow: 4px 0 10px rgba(var(--v-shadow-key-umbra-opacity), 0.05) !important;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
}

:deep(.v-list-group__header.v-list-item--active) {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
